import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { theme } from '../../theme/theme';
import { Badge, Checkbox } from './';

const useCheckCardClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        borderRadius: 5,
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        background: 'white',
        transition: `all ${theme.transitions.fast}s`,
        '&:not(.editable)': {
            padding: 20,
        },
        '&.editable': {
            padding: 10,
        },
        '&.editable.checked': {
            boxShadow: `0 0 0 1px ${theme.colors.green[4]}`,
            background: theme.colors.neutral[4],
        },
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        '& > span:first-child': {
            margin: -6,
        },
    },
    labels: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flex: 1,
        '& > div:first-child': {
            marginBottom: 10,
        },
        '& > span:first-of-type': {
            color: theme.colors.darkBlue[1],
            ...theme.typography.headings.h5,
            marginBottom: 0,
            textTransform: 'capitalize',
        },
        '& > span:nth-of-type(2)': {
            color: theme.colors.neutral[2],
            ...theme.typography.content.caption.paragraph,
        },
    },
    children: {
        display: 'flex',
    },
});

/**
 * Card with checkbox, badge, title and subtitle used in some pages.
 *
 * @param props.badge object containing badge props
 * @param props.title activity name
 * @param props.subtitle years elapsed since last inspection
 * @param props.editable if card can be checked or unchecked
 * @param props.checked if card is checked
 * @param props.onCheck callback triggered when the checkbox is clicked
 * @param props.aside children content to be rendered beside the header
 * @param props.children children content to be rendered below the header
 */
export const CheckCard = (
    props: JSX.IntrinsicElements['div'] & {
        badge?: Parameters<typeof Badge>[0];
        title: string;
        subtitle?: string;
        editable?: boolean;
        checked?: boolean;
        onCheck?: (checked: boolean) => void;
        aside?: React.ReactNode;
        children?: React.ReactNode;
    },
) => {
    const { badge, title, subtitle, editable = false, checked = false, onCheck, ...rootProps } = props;
    const classes = useCheckCardClasses();
    const cls = { editable, checked };

    return (
        <div {...rootProps} className={clsx(classes.root, cls, rootProps.className)}>
            <div className={classes.header}>
                {props.editable && <Checkbox checked={checked} onChange={event => onCheck?.(event.target.checked)} />}
                <div className={classes.labels}>
                    {badge && <Badge {...badge} />}
                    <span>{title}</span>
                    {subtitle != undefined && <span>{subtitle}</span>}
                </div>
                {props.aside && <div className={classes.children}>{props.aside}</div>}
            </div>
            {props.children && <div className={classes.children}>{props.children}</div>}
        </div>
    );
};
